import { cva, type VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../utils/className';

const buttonVariants = cva(
  // Base styles
  [
    'inline-flex items-center justify-center',
    'font-medium size-p2 leading-5.5',
    'transition-all duration-150 ease-in-out',
    'disabled:cursor-not-allowed',
    'cursor-pointer',
    'hover:brightness-110 hover:opacity-90',
    'active:brightness-95',
  ],
  {
    variants: {
      variant: {
        primary: ['px-3 py-2 rounded-lg', 'bg-surface-focus text-inverted'],
        secondary: [
          'px-3 py-2 rounded-lg',
          'bg-surface-primary border border-border-primary',
          'text-primary',
        ],
        tertiary: [
          'underline decoration-solid',
          '[text-decoration-skip-ink:none] [text-underline-position:from-font]',
          'font-regular',
          'text-primary',
          // Tertiary needs different hover since it's text-only
          'hover:opacity-70 hover:brightness-100',
        ],
      },
      state: {
        default: '',
        negative: '',
        disabled: [
          'pointer-events-none',
          // Override hover/active states for disabled
          'hover:brightness-100 hover:opacity-100 ',
          'active:brightness-100 active:opacity-100',
        ],
      },
    },
    compoundVariants: [
      // Primary negative state
      {
        variant: 'primary',
        state: 'negative',
        class: ['bg-surface-negative text-inverted'],
      },
      // Primary disabled state
      {
        variant: 'primary',
        state: 'disabled',
        class: [
          'bg-surface-disabled-support text-disabled border border-border-secondary',
        ],
      },
      // Secondary negative state
      {
        variant: 'secondary',
        state: 'negative',
        class: ['text-negative border-border-negative'],
      },
      // Secondary disabled state
      {
        variant: 'secondary',
        state: 'disabled',
        class: [
          'text-disabled border-border-disabled',
          'bg-surface-disabled-support',
        ],
      },
      // Tertiary negative state
      {
        variant: 'tertiary',
        state: 'negative',
        class: ['text-negative'],
      },
      // Tertiary disabled state
      {
        variant: 'tertiary',
        state: 'disabled',
        class: ['text-disabled'],
      },
    ],
    defaultVariants: {
      variant: 'primary',
      state: 'default',
    },
  }
);

export interface ButtonProps
  extends
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>,
    VariantProps<typeof buttonVariants> {
  /**
   * Button label text
   */
  children: ReactNode;

  /**
   * Optional leading icon element
   */
  leadingIcon?: ReactNode;

  /**
   * Optional trailing icon element
   */
  trailingIcon?: ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Button Component
 *
 * A flexible button component with multiple variants and states.
 * Supports Primary, Secondary, and Tertiary styles with Default, Negative, and Disabled states.
 *
 * @example
 * ```tsx
 * <Button variant="primary">Click me</Button>
 * <Button variant="secondary" state="negative">Delete</Button>
 * <Button variant="tertiary" leadingIcon={<Icon />}>With Icon</Button>
 * ```
 */
export function Button({
  variant = 'primary',
  state = 'default',
  children,
  leadingIcon,
  trailingIcon,
  className,
  ...props
}: ButtonProps) {
  const isDisabled = state === 'disabled';

  return (
    <button
      className={cn(buttonVariants({ variant, state }), className)}
      disabled={isDisabled}
      {...props}
    >
      {leadingIcon && (
        <span className='inline-flex h-4 w-4 items-center justify-center'>
          {leadingIcon}
        </span>
      )}
      <span>{children}</span>
      {trailingIcon && (
        <span className='inline-flex h-4 w-4 items-center justify-center'>
          {trailingIcon}
        </span>
      )}
    </button>
  );
}
